/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'next-i18next';
import { API, APIRoutes } from '@utils/api';
import { Pagination } from '@styles/globalStyles';
import routes from '@utils/routes';

import { Container, Wrapper, Offers } from '@components/Offers/OffersList/styles';

import OffersFilters from '@components/OffersFilters';
import OfferBox from '@components/OfferBox';

async function fetchOffers(filters, page = 1) {
  const { data } = await API.get(APIRoutes.offers({ page, ...filters }));
  return data;
}

function OffersList({ offers }) {
  const { t } = useTranslation('offers');
  const {
    meta: {
      pagination: { objects, currentPage, pages },
    },
  } = offers || {};

  const [page, setPage] = useState(parseInt(currentPage, 10));
  const [pagesCount, setPagesCount] = useState(pages);
  const [offersCount, setOffersCount] = useState(objects);
  const [filters, setFilters] = useState({
    companyPosition: null,
    industry: null,
    workModel: null,
    practiceType: null,
  });
  const offersRef = useRef(null);
  const isMounted = useRef(false);
  const router = useRouter();
  const queryClient = useQueryClient();

  let debounce: ReturnType<typeof setTimeout>;

  function onSubmit(query) {
    clearTimeout(debounce);

    debounce = setTimeout(async () => {
      const {
        offer: { industry, companyPosition, workModel, practiceType },
      } = query;

      setFilters({
        industry,
        companyPosition,
        workModel,
        practiceType,
      });
      const params = {
        ...(industry ? { industry } : {}),
        ...(companyPosition ? { companyPosition } : {}),
        ...(workModel ? { workModel } : {}),
        ...(practiceType ? { practiceType } : {}),
      };
      await router.replace({ pathname: routes.offers, query: params }, undefined, {
        shallow: true,
      });
      setPage(1);
    }, 300);
  }

  const { data, isFetching } = useQuery(['offers', filters], () => fetchOffers(filters, page), {
    enabled: false,
    initialData: offers,
    keepPreviousData: true,
  });

  useEffect(() => {
    setPagesCount(data.meta.pagination.pages);
    setPage(data.meta.pagination.currentPage);
    setOffersCount(data.meta.pagination.objects);
  }, [data, filters]);

  useEffect(() => {
    if (isMounted.current) {
      queryClient.prefetchQuery(['offers', filters], () => fetchOffers(filters, page));
    } else {
      isMounted.current = true;
    }
  }, [filters, page, queryClient]);

  const handlePageClick = async (event) => {
    const pageNumber = event.selected + 1;
    await router.push(`?page=${pageNumber}`, undefined, { shallow: true });
    setPage(pageNumber);

    offersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    offersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <Container ref={offersRef} id="offers">
      <Wrapper>
        <OffersFilters onSubmit={onSubmit} offersCount={offersCount} resetable />

        <Offers>
          {data?.offers &&
            data?.offers.map((offer) => (
              <OfferBox key={`offer-box_${offer.id}`} offer={offer} isFetching={isFetching} />
            ))}
        </Offers>
        <Pagination
          breakLabel="..."
          nextLabel={t('Next')}
          onPageChange={handlePageClick}
          pageRangeDisplayed={2}
          initialPage={page - 1}
          disableInitialCallback
          pageCount={pagesCount}
          previousLabel={t('Previous')}
          renderOnZeroPageCount={null}
        />
      </Wrapper>
    </Container>
  );
}

export default OffersList;
